<template>
    <q-tab-panel name="EventManagementTab">
        <div class="q-mb-md">
            {{ ts('saveToFileHint') }}
        </div>

        <q-form autofocus>
            <q-btn
                :label="ts('saveToFile')"
                outline
                color="secondary"
                @click="saveToFile()"
            />
            <q-btn class="q-ml-lg" :label="ts('saveToCloud')" outline color="secondary" @click="saveToCloud()" />
            <q-btn class="q-ml-lg" :label="ts('saveToCsj')" outline color="secondary" @click="saveToCsj()" />
            <q-btn class="q-ml-lg" :label="ts('exportToXlsx')" outline color="secondary" @click="exportEventToXlsx()" />
        </q-form>

        <div class="q-mt-xl q-mb-md">
            {{ ts('sendToCsjHint') }}
        </div>

        <q-form>
            <q-btn
                :label="ts('sendToCsj')"
                outline
                color="primary"
                @click="sendToCsj()"
            />
            <q-btn
                class="q-ml-lg"
                :label="ts('sendToWs')"
                outline
                color="primary"
                v-if="wsIdRequired"
                @click="sendToWs()"
            />
        </q-form>

        <div class="q-mt-xl q-mb-md">
            {{ ts('removeEventHint') }}
        </div>

        <q-form>
            <q-btn
                :label="ts('removeEvent')"
                outline
                color="negative"
                @click="removeEvent()"
            />
        </q-form>
    </q-tab-panel>
</template>

<script setup lang="ts">
import Confirmation from '@/components/dialogs/Confirmation.vue'
import { authenticate, checkAuth, showError } from '@/network/auth'
import { encodeBoatClass } from '@/network/decoders'
import { exportEvent } from '@/network/exporter'
import apiRequest from '@/network/httpClient'
import { individualLegendDef } from '@/orm/catalogs/OtherResults'
import EventsRepo from '@/orm/repositories/EventsRepo'
import { DateFormat, ds, ns, tcs, ts } from '@/plugins/i18nFormat'
import InfoBox from '@dialogs/InfoBox.vue'
import IssueList from '@dialogs/IssueList.vue'
import LogIn from '@dialogs/LogIn.vue'
import EventResult from '@model/EventResult'
import { Defaults, Options } from '@model/Setting'
import SettingsRepo from '@repo/SettingsRepo'
import { createExcelSchema, createWorkbook, spreadsheetThemes } from 'typed-xlsx'
import { saveAs } from 'file-saver'
import { isPositiveNumber } from 'is-what'
import deterministicStringify from 'json-stringify-deterministic'
import { useRepo } from 'pinia-orm'
import { QSpinnerGears, useQuasar } from 'quasar'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { create } from 'xmlbuilder2'
import { localization } from '@/plugins/i18n'
import nvl from '@/utils/nvl'

const quasar = useQuasar()
const router = useRouter()
const route = useRoute()

const emits = defineEmits(['saveEventToCloud'])

const props = defineProps<{ event: any }>()
const event = computed(() => (props.event ?? useRepo(EventsRepo).getEvent(String(route.params.eventId), true)))

const wsIdRequired = computed(() => (event.value.wsEventId !== null && event.value.wsEventId.length > 0))

const isAuthenticated = computed(() => (useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken) !== ''))

async function exportEventToXlsx() {
    const originalLocale = localization.global.locale.value

    if (event.value.forceEnglish)
        localization.global.locale.value = 'en'

    try {
        const checkedEvent = useRepo(EventsRepo).getEvent(event.value.id, true)

        const boats = [...checkedEvent.boats].sort((a, b) => {
            const classA = a.eventClass?.eventClassName ?? ''
            const classB = b.eventClass?.eventClassName ?? ''
            const classCmp = classA.localeCompare(classB)

            if (classCmp !== 0) return classCmp

            return a.sailNoSortable.localeCompare(b.sailNoSortable)
        })

        const rows = []

        for (const boat of boats) {
            const useBoatNames = boat.eventClass?.boatClass?.useBoatNames ?? false
            const boatName = useBoatNames ? nvl(boat.name, '') : ''

            rows.push({
                boatClass: boat.eventClass?.eventClassName ?? '',
                sailNo: boat.sailNo,
                boatName,
                category: boat.orderedCategories.map(c => c.visibleShortcut).join(', '),
                detail: nvl(boat.detail, ''),
                handicap: (boat.eventClass?.handicapSystem !== null) ? boat.handicapShortcut : '',
                crew: boat.orderedCrewList.map(crew => ({
                    regId: crew.regId ?? '',
                    wsId: crew.wsId ?? '',
                    fullName: crew.reverseFullName,
                    birthYear: crew.birthYear ?? '',
                    sex: crew.sex ? ts(crew.sex) : '',
                })),
            })
        }

        const showBoatName = boats.some(boat => boat.eventClass?.boatClass?.useBoatNames)
        const showHandicap = boats.some(boat => boat.eventClass?.handicapSystem !== null)

        const schema = createExcelSchema<any>()
            .column('boatClass', { header: ts('boatClass'), accessor: 'boatClass' })
            .column('sailNo', { header: ts('sailNo'), accessor: 'sailNo' })

        if (showBoatName)
            schema.column('boatName', { header: ts('boatName'), accessor: 'boatName' })

        schema.column('category', { header: ts('category'), accessor: 'category' })
            .column('detail', { header: event.value.descInResultsTitle ?? ts('note'), accessor: 'detail' })

        if (showHandicap)
            schema.column('handicap', {
                header: ts('handicap'),
                accessor: 'handicap',
                style: { alignment: { horizontal: 'center' } },
            })

        schema.column('regId', { header: ts('regId'), accessor: (row) => row.crew.map((c) => c.regId) })

        if (wsIdRequired.value)
            schema.column('wsId', { header: ts('wsId'), accessor: (row) => row.crew.map((c) => c.wsId) })

        schema.column('fullName', { header: ts('fullName'), accessor: (row) => row.crew.map((c) => c.fullName) })
            .column('birthYear', { header: ts('birthYear'), accessor: (row) => row.crew.map((c) => c.birthYear) })
            .column('sex', {
                header: ts('sex'),
                accessor: (row) => row.crew.map((c) => c.sex),
                style: { alignment: { horizontal: 'center' } },
            })

        const buildedSchema = schema.build()

        const workbook = createWorkbook()

        workbook.sheet(ts('crewListShortcut'), { freezePane: { rows: 1 } }).table('startovka', {
            rows: rows,
            schema: buildedSchema,
            theme: spreadsheetThemes.classic,
        })

        const availableRaces = checkedEvent.availableRaces()
        const attendedClasses = checkedEvent.orderedClasses

        const allClassResults = attendedClasses.map(eventClass => ({
            eventClass,
            results: checkedEvent.results(eventClass.boatClassId),
        }))

        for (const race of availableRaces) {
            const raceRows = []
            let showHandicapInRace = false

            for (const classData of allClassResults) {
                const eventClass = classData.eventClass
                const classResults = classData.results

                // Prepare data for sorting
                const boatRaceResults = []

                for (const boatResult of classResults) {
                    const boat = boatResult.boat

                    const raceResult = boatResult.results.find(r => r.race === race.race)

                    if (!raceResult) continue

                    boatRaceResults.push({ boat, boatResult, raceResult })
                }

                // Sorting logic:
                // 1. Finish time (realTime for handicaps, but generally finishTime)
                // 2. Points ascending
                boatRaceResults.sort((a, b) => {
                    const timeA = a.raceResult.details?.finishTime ?? Number.MAX_SAFE_INTEGER
                    const timeB = b.raceResult.details?.finishTime ?? Number.MAX_SAFE_INTEGER

                    if (timeA !== timeB) return timeA - timeB

                    const pointsA = a.raceResult.points ?? 0
                    const pointsB = b.raceResult.points ?? 0

                    return pointsA - pointsB
                })

                let previousRealTime: number | null = null

                for (const { boat, raceResult } of boatRaceResults) {
                    const useBoatNames = boat?.eventClass?.boatClass?.useBoatNames ?? false
                    const boatName = useBoatNames ? nvl(boat?.name, '') : ''
                    const boatClass = boat?.eventClass?.eventClassName ?? ''

                    const startTime = race.startTime(boat?.boatClassId, false, boat?.id)
                    const isHandicapBoat = boat?.eventClass?.handicapSystem !== null

                    if (isHandicapBoat)
                        showHandicapInRace = true

                    const currentRealTime = raceResult.details?.realTime(startTime)

                    let offset = ''

                    if (
                        currentRealTime !== null && currentRealTime !== undefined && previousRealTime !== null
                        && previousRealTime !== undefined
                    ) {
                        offset = (currentRealTime - previousRealTime).toString()
                    }

                    previousRealTime = (currentRealTime !== null && currentRealTime !== undefined)
                        ? currentRealTime
                        : null

                    raceRows.push({
                        boatClass,
                        sailNo: boat?.sailNo ?? '',
                        boatName,
                        captain: boat?.orderedCrewList?.[0]?.reverseFullName ?? '',
                        handicap: isHandicapBoat ? boat?.handicapShortcut : '',
                        startTime: isHandicapBoat ? ds(startTime, DateFormat.Time) : '',
                        finishTime: isHandicapBoat ? ds(raceResult.details?.finishTime, DateFormat.Time) : '',
                        realTime: isHandicapBoat ? currentRealTime : '',
                        offset,
                        coefficient: isHandicapBoat
                            ? (Math.round(boat?.calcHandicapCoef(boat?.eventClass, race.id) * 10000) / 10000)
                            : '',
                        calculatedTime: isHandicapBoat ? raceResult.details?.calculatedTime(startTime) : '',
                        result: raceResult.result,
                        attr: nvl(raceResult.details?.attr, ''),
                        points: nvl(raceResult.points, 0).toFixed(1),
                        discarded: raceResult.isDiscarded ? ts('discarded') : '',
                    })
                }
            }

            const raceSchema = createExcelSchema<any>()
                .column('boatClass', { header: ts('boatClass'), accessor: 'boatClass' })
                .column('sailNo', { header: ts('sailNo'), accessor: 'sailNo' })

            if (showBoatName)
                raceSchema.column('boatName', { header: ts('boatName'), accessor: 'boatName' })

            raceSchema.column('captain', { header: ts('captain'), accessor: 'captain' })

            if (showHandicapInRace) {
                raceSchema.column('handicap', {
                    header: ts('handicap'),
                    accessor: 'handicap',
                    style: { alignment: { horizontal: 'center' } },
                })
                    .column('startTime', {
                        header: ts('startTime'),
                        accessor: 'startTime',
                        style: { alignment: { horizontal: 'center' } },
                    })
                    .column('finishTime', {
                        header: ts('finishTime'),
                        accessor: 'finishTime',
                        style: { alignment: { horizontal: 'center' } },
                    })
                    .column('realTime', { header: ts('realTimeS'), accessor: 'realTime' })
                    .column('offset', {
                        header: ts('offsetS'),
                        accessor: 'offset',
                        style: { alignment: { horizontal: 'right' } },
                    })
                    .column('coefficient', { header: ts('coefficient'), accessor: 'coefficient' })
                    .column('calculatedTime', { header: ts('calculatedTimeS'), accessor: 'calculatedTime' })
            }

            raceSchema.column('result', { header: ts('result'), accessor: 'result' })
                .column('attr', {
                    header: ts('attribute'),
                    accessor: 'attr',
                    style: { alignment: { horizontal: 'right' } },
                })
                .column('points', {
                    header: ts('points'),
                    accessor: 'points',
                    style: { alignment: { horizontal: 'right' } },
                })
                .column('discarded', {
                    header: ts('discarded'),
                    accessor: 'discarded',
                    style: { alignment: { horizontal: 'center' } },
                })

            workbook.sheet('R' + race.race, { freezePane: { rows: 1 } }).table('race' + race.race, {
                rows: raceRows,
                schema: raceSchema.build(),
                theme: spreadsheetThemes.classic,
            })
        }

        for (const classData of allClassResults) {
            const eventClass = classData.eventClass
            const classResults = classData.results
            const useBoatNames = eventClass.boatClass?.useBoatNames ?? false
            const classAvailableRaces = checkedEvent.availableRaces(eventClass.boatClassId)

            if (classAvailableRaces.length === 0) {
                continue
            }

            const classRows = classResults.map((boatResult, index) => {
                const boat = boatResult.boat
                const boatName = useBoatNames ? nvl(boat?.name, '') : ''

                const row: any = {
                    index: index + 1,
                    sailNo: boat?.sailNo ?? '',
                    boatName,
                    captain: boat?.orderedCrewList?.[0]?.reverseFullName ?? '',
                    totalPoints: nvl(boatResult.points, 0).toFixed(1),
                    logPoints: nvl(boatResult.logPoints, 0).toFixed(0),
                }

                // Add points for each race
                classAvailableRaces.forEach((race, raceIdx) => {
                    const raceResult = boatResult.results.find(r => r.race === race.race)
                    if (raceResult) {
                        let res = raceResult.result

                        const isPositiveNumber = (val: any) => typeof val === 'number' && val > 0
                        if (isPositiveNumber(res) && (10 * (res as number)) % 10 !== 0) {
                            res = ns(res, 'points')
                        }

                        row['R' + (raceIdx + 1)] = `${res}${raceResult.isDiscarded ? '*' : ''}`
                    } else {
                        row['R' + (raceIdx + 1)] = ''
                    }
                })

                return row
            })

            const classSchema = createExcelSchema<any>()
                .column('index', {
                    header: ts('orderShortcut'),
                    accessor: 'index',
                    style: { alignment: { horizontal: 'right' } },
                })
                .column('sailNo', { header: ts('sailNo'), accessor: 'sailNo' })

            if (useBoatNames) {
                classSchema.column('boatName', { header: ts('boatName'), accessor: 'boatName' })
            }

            classSchema.column('captain', { header: ts('captain'), accessor: 'captain' })

            classAvailableRaces.forEach((_, raceIdx) => {
                const rKey = 'R' + (raceIdx + 1)
                classSchema.column(rKey, {
                    header: rKey,
                    accessor: rKey,
                    style: { alignment: { horizontal: 'right' } },
                })
            })

            classSchema.column('totalPoints', {
                header: ts('total'),
                accessor: 'totalPoints',
                style: { alignment: { horizontal: 'right' }, font: { bold: true } },
            })
                .column('logPoints', {
                    header: ts('logPoints'),
                    accessor: 'logPoints',
                    style: { alignment: { horizontal: 'right' } },
                })

            workbook.sheet(eventClass.eventClassName, { freezePane: { rows: 1 } }).table('class' + eventClass.id, {
                rows: classRows,
                schema: classSchema.build(),
                theme: spreadsheetThemes.classic,
            })
        }

        const buffer = await workbook.toBuffer()

        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

        saveAs(blob, 'event-' + checkedEvent.ctlId + '.xlsx')
    } finally {
        localization.global.locale.value = originalLocale
    }
}

function removeEvent() {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('reallyRemoveEvent', { eventTitle: event.value.fullTitle }),
        },
    }).onOk(() => {
        useRepo(EventsRepo).destroy(event.value.id)

        router.push({ name: 'MainPage' })
    })
}

function saveToFile() {
    const data = JSON.stringify(exportEvent(event.value))

    const blob = new Blob([data], { type: 'application/json; charset=utf-8' })

    event.value?.setModified(false)

    saveAs(blob, 'event-' + event.value.ctlId + '.json')
}

function saveToCloud() {
    emits('saveEventToCloud')
}

async function saveToCsj(ev) {
    await sendToCsj(ev, true)
}

async function sendToCsj(ev, provisional = false) {
    const issueList = []

    const checkedEvent = useRepo(EventsRepo).getEvent(event.value.id, true)

    if (checkedEvent.title.trim() === '')
        issueList.push({ severity: 'crit', what: ts('noEventTitle') })

    if (
        String(checkedEvent.ctlId).length !== 6
        || String(checkedEvent.ctlId).substring(0, 2) !== String(new Date().getFullYear()).substring(2, 4)
    )
        issueList.push({ severity: 'crit', what: ts('badCtlIdFormat', { ctlId: checkedEvent.ctlId }) })

    if (checkedEvent.to !== null && checkedEvent.from > checkedEvent.to)
        issueList.push({ severity: 'crit', what: ts('invalidEventTimespan') })

    if (checkedEvent.clubId === null && (checkedEvent.organizer ?? '').trim() === '')
        issueList.push({ severity: 'crit', what: ts('invalidOrganizer') })

    if (checkedEvent.venueId === null && (checkedEvent.otherVenue ?? '').trim() === '')
        issueList.push({ severity: 'crit', what: ts('invalidVenue') })

    if (!provisional && (checkedEvent.directorName ?? '').trim() === '')
        issueList.push({ severity: 'crit', what: ts('invalidDirector') })

    if (checkedEvent.classes.length === 0)
        issueList.push({ severity: 'crit', what: ts('noSelectedClasses') })

    if (!provisional) {
        if (checkedEvent.referees.length === 0)
            issueList.push({ severity: 'crit', what: ts('noSelectedReferee') })
        else {
            if (!checkedEvent.referees.some((referee) => referee.refereeRole.isMainReferee) && !checkedEvent.useCourses)
                issueList.push({ severity: 'crit', what: ts('noMainReferee') })
            else if (checkedEvent.referees.filter((referee) => referee.refereeRole.isMainReferee).length > 1)
                issueList.push({ severity: 'crit', what: ts('moreThanOneMainReferee') })

            if (
                checkedEvent.useCourses
                && checkedEvent.referees.filter((referee) => referee.refereeRole.isMainCourseReferee).length
                    !== checkedEvent.courses.length
            )
                issueList.push({ severity: 'crit', what: ts('incorrectNumberOfCoursesReferees') })
        }

        if (checkedEvent.referees.filter((referee) => referee.refereeRole.isChiefUmpire).length > 1)
            issueList.push({ severity: 'crit', what: ts('moreThanOneChiefUmpire') })

        if (checkedEvent.referees.filter((referee) => referee.refereeRole.isJuryChairman).length > 1)
            issueList.push({ severity: 'crit', what: ts('moreThanOneJuryChairman') })

        checkedEvent.referees.filter((r) => r.refereeId !== null).forEach(function(referee) {
            if (checkedEvent.referees.filter((r) => r.refereeId === referee.refereeId).length > 1)
                issueList.push({
                    severity: 'crit',
                    what: ts('refereeWithMoreRoles', { referee: referee.referee.fullNameWithIdentification }),
                })
        })

        if (checkedEvent.lifeguards.length === 0 && !checkedEvent.noLifeguards)
            issueList.push({ severity: 'crit', what: ts('noSelectedLifeguards') })
        else if (
            !checkedEvent.noLifeguards
            && !checkedEvent.lifeguards.some((lifeguard) => lifeguard.lifeguardRole.isMainLifeguard)
        )
            issueList.push({ severity: 'warn', what: ts('noMainLifeguard') })
        else if (
            !checkedEvent.noLifeguards
            && checkedEvent.lifeguards.filter((lifeguard) => lifeguard.lifeguardRole.isMainLifeguard).length > 1
        )
            issueList.push({ severity: 'crit', what: ts('moreThanOneMainLifeguard') })

        checkedEvent.lifeguards.forEach(function(lifeguard) {
            if (checkedEvent.lifeguards.filter((l) => l.lifeguardId === lifeguard.lifeguardId).length > 1)
                issueList.push({
                    severity: 'crit',
                    what: ts('lifeguardWithMoreRoles', { lifeguard: lifeguard.lifeguard.fullNameWithIdentification }),
                })
        })
    }

    if (checkedEvent.boats.length === 0)
        issueList.push({ severity: 'crit', what: ts('noRegisteredBoats') })

    if (checkedEvent.races.length === 0)
        issueList.push({ severity: 'warn', what: ts('noRacesOnEvent') })

    if (checkedEvent.boats.some((boat) => boat.crew.length === 0))
        issueList.push({ severity: 'crit', what: ts('boatWithoutCrew') })

    if (checkedEvent.boats.some((boat) => boat.isLicenseIssue))
        issueList.push({ severity: 'warn', what: ts('licenseIssueOnEvent') })

    if (
        checkedEvent.boats.some((boat) =>
            boat.crew.filter((c) => c.isPublic).length === boat.crew.length && boat.crew.length > 0
        )
    )
        issueList.push({ severity: 'crit', what: ts('onlyPublicBoat') })

    checkedEvent.classes.forEach(function(eventClass) {
        if (checkedEvent.boats.filter((boat) => boat.boatClassId === eventClass.boatClassId).length === 0)
            issueList.push({
                severity: 'warn',
                what: ts('classWithoutBoats', { boatClass: eventClass.boatClass.name }),
            })
        else if (
            useRepo(EventResult).whereHas(
                'boat',
                (q) => q.where('eventId', checkedEvent.id).where('boatClassId', eventClass.boatClassId),
            ).get().length === 0
        )
            issueList.push({ severity: 'warn', what: ts('noResultsInClass', { boatClass: eventClass.boatClass.name }) })
        else if (checkedEvent.results(eventClass.boatClassId).some((boat) => boat.results.some(r => !r.decided)))
            issueList.push({
                severity: 'warn',
                what: ts('decidedResultsInClass', { boatClass: eventClass.boatClass.name }),
            })

        if (eventClass.publicMode === 1 && checkedEvent.boats.some((boat) => boat.captain?.isPublic))
            issueList.push({ severity: 'crit', what: ts('publicType1Violated') })

        if (
            eventClass.weight >= 2 && !eventClass.isLongDistance
            && checkedEvent.availableRaces(eventClass.boatClassId).length === 1
        )
            issueList.push({
                severity: 'warn',
                what: ts('singleRaceHigherCoefficient', { boatClass: eventClass.boatClass.name }),
            })
    })

    /** provisional -> nejsou kriticke -> jdeme rovnou dal -> dodelat ! */

    let canPass = false

    if (issueList.length === 0) {
        issueList.push({ severity: 'ok', what: ts('everythingOk') })
        canPass = true
    } else if (!issueList.some((i) => i.severity === 'crit')) {
        issueList.push({ severity: 'ok', what: ts('noCritIssues') })
        canPass = true
    }

    if (provisional && canPass) {
        await performUpload(true)

        event.value?.setModified(false)
    } else {
        quasar.dialog({
            component: IssueList,
            componentProps: {
                issueList: issueList,
            },
        })
            .onOk(async function() {
                if (await checkAuth() !== 'auth') {
                    await logIn(3, 0, provisional)

                    return
                }

                await performUpload(provisional)

                if (provisional) {
                    event.value?.setModified(false)
                }
            })
    }
}

function logIn(attempts = 3, error = 0, provisional = false) {
    quasar.dialog({
        component: LogIn,
        componentProps: {
            errorMsg: showError(error),
        },
    }).onOk(async (payload) => {
        const lastError = await authenticate(payload.username, payload.password)

        if (!isAuthenticated.value) {
            if (attempts > 1)
                logIn(attempts - 1, lastError, provisional)
            else
                quasar.dialog({
                    component: InfoBox,
                    componentProps: {
                        message: ts('tooManyAttempts'),
                    },
                })
        } else {
            await performUpload(provisional)

            if (provisional) {
                event.value?.setModified(false)
            }
        }
    })
}

async function computeDigest(data) {
    const payload = {
        message: data,
        sharedSecret:
            'Mwvry6K9D4mgqP9lEqAxQuLiS3AWGhPbkIbCienvtYdnYKe6mpwmMbKMt8zypNTgdJbGlGRg5ZM6fUxeVvGw0sCUeGyINFBNslogyP2aUJdB33fc1ILd3FrJbLDcyzOr1nezjjqTHxeZFYRKiz27hiLelx01RsaU0A1M9WU0ulhCIHrZ7CMKDKozjOSQvoGSri3wntThOzZ96tYDrJPOdxYsNztOeAshk3MJB7VBCeMV1rxF1TetJZGTxuvFvlRSIrufLveTTERLGbNapgQO8Ef506xnAfHwXUyZIfV3mXaK',
    }

    const msgUint8 = new TextEncoder().encode(deterministicStringify(payload))

    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8)

    const hashArray = Array.from(new Uint8Array(hashBuffer))

    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

async function performUpload(provisional = false) {
    const notif = quasar.notify({
        group: false,
        type: 'secondary',
        message: ts(provisional ? 'provisionalResultsUploading' : 'resultsUploading'),
        spinner: QSpinnerGears,
        timeout: 0,
        multiLine: true,
        classes: 'q-pa-lg text-subtitle1',
    })

    const checkedEvent = useRepo(EventsRepo).getEvent(event.value.id, true)

    if (checkedEvent === null)
        return

    const data = JSON.stringify(exportEvent(checkedEvent))

    let payload = {
        event: {
            id: checkedEvent.ctlId,
            name: checkedEvent.title.trim(),
            clubId: checkedEvent.clubId,
            organizer: checkedEvent.organizer === null ? null : checkedEvent.organizer.trim(),
            venueId: checkedEvent.venueId,
            otherVenue: checkedEvent.otherVenue === null ? null : checkedEvent.otherVenue.trim(),
            sponsor: checkedEvent.sponsor?.trim(),
            from: ds(checkedEvent.from, DateFormat.SystemDate),
            to: checkedEvent.to === null ? null : ds(checkedEvent.to, DateFormat.SystemDate),
            days: checkedEvent.to === null ? 1 : null,
            classes: checkedEvent.classes.map((eventClass) => ({
                classId: encodeBoatClass(eventClass.boatClassId),
                weight: eventClass.weight,
                eventType: eventClass.eventType,
                shortcut: eventClass.shortcut,
                discardDefinition: eventClass.discardDefinition,
                className: eventClass.className,
                longDistance: eventClass.isLongDistance ? 1 : null,
                raceMode: eventClass.isElimination ? 'elimination' : 'common',
            })),
            courses: checkedEvent.courses.map((course) => ({
                title: course.title,
            })),
            regattaDirector: {
                name: checkedEvent.directorName?.trim(),
                memberId: (checkedEvent.directorId !== null && checkedEvent.directorMember !== null)
                    ? Number(checkedEvent.directorMember.regId?.replace('-', ''))
                    : null,
            },
            regattaGuarantor: {
                name: checkedEvent.guarantorName?.trim(),
                memberId: (checkedEvent.guarantorId !== null && checkedEvent.guarantorMember !== null)
                    ? Number(checkedEvent.guarantorMember.regId?.replace('-', ''))
                    : null,
            },
            referees: checkedEvent.referees.filter((referee) => (referee.refereeId !== null)).map((referee) => ({
                refereeId: referee.referee.id,
                roleId: referee.refereeRole.id,
                memberId: referee.referee.regId.replace('-', ''),
            })),
            lifeguards: checkedEvent.lifeguards.map((lifeguard) => ({
                lifeguardId: lifeguard.lifeguard.id,
                roleId: lifeguard.lifeguardRole.id,
                memberId: lifeguard.lifeguard.regId?.replace('-', ''),
            })),
            visual: {
                boatNoteTitle: checkedEvent.descInResultsTitle?.trim().length > 0
                    ? checkedEvent.descInResultsTitle?.trim()
                    : null,
                crewNoteTitle: checkedEvent.crewDescInResultsTitle?.trim().length > 0
                    ? checkedEvent.crewDescInResultsTitle?.trim()
                    : null,
            },
        },
        results: checkedEvent.classes.map((eventClass) => ({
            classId: encodeBoatClass(eventClass.boatClassId),
            longDistance: eventClass.isLongDistance ? 1 : null,
            raceMode: eventClass.isElimination ? 'elimination' : 'common',
            plannedCoefficient: eventClass.weight,
            calculatedCoefficient: eventClass.calculatedWeight,
            categories: eventClass.orderedCategories.map(category => ({
                shortcut: category.visibleShortcutCz,
                name: category.visibleTitleCz,
                shortcutEn: category.visibleShortcutEn,
                nameEn: category.visibleTitleEn,
                sexLimit: category.sex,
                ageUnderLimit: category.ageUnder,
                ageOverLimit: category.ageAbove,
                otherLimit: category.otherLimit,
            })),
            scoreboard: checkedEvent.results(eventClass.boatClassId).map((boat) => ({
                country: boat.boat.country,
                sailNo: boat.boat.sailNumber,
                name: boat.boat.name,
                note: boat.boat.detail,
                categories: boat.boat.categories.map(category => ({
                    category: category.visibleShortcutCz,
                })),
                rank: boat.index,
                points: boat.points,
                logPoints: boat.logPoints,
                races: boat.results.map((result) => ({
                    rank: result.details?.order,
                    points: result.points,
                    other: result.decided ? result.details?.other : 'DNC',
                    attr: result.details?.attr,
                    discarded: result.isDiscarded,
                    guessed: !result.decided,
                    formula: Object.hasOwn(individualLegendDef, result.details?.other)
                        ? individualLegendDef[result.details?.other]
                            .replace('base', result.details?.orderInclHandicap ?? 0)
                            .replace(
                                'total',
                                tcs('pointsNoShort', result.points, {
                                    locale: 'cs',
                                    named: { n: ns(result.points, 'points', 'cs') },
                                }),
                            )
                            .replace('attr %', ((result.details?.attr ?? 0) * 100) + ' %')
                            .replace('attr', result.details?.attr ?? 0)
                        : null,
                    formulaEn: Object.hasOwn(individualLegendDef, result.details?.other)
                        ? individualLegendDef[result.details?.other]
                            .replace('base', result.details?.orderInclHandicap ?? 0)
                            .replace(
                                'total',
                                tcs('pointsNoShort', result.points, {
                                    locale: 'en',
                                    named: { n: ns(result.points, 'points', 'en') },
                                }),
                            )
                            .replace('attr %', ((result.details?.attr ?? 0) * 100) + ' %')
                            .replace('attr', result.details?.attr ?? 0)
                        : null,
                })),
                crew: boat.boat.crew.map((crew) => ({
                    memberId: Number(crew.regId.replace('-', '')),
                    firstName: crew.firstName.trim(),
                    lastName: crew.lastName.trim(),
                    birthYear: crew.birthYear,
                    sex: crew.sex,
                    isCaptain: crew.captain,
                    phone: (crew.phone === undefined || crew.phone === null) ? null : crew.phone.trim(),
                    email: (crew.email === undefined || crew.email === null) ? null : crew.email.trim(),
                    citizenship: crew.nonMemberCitizenship ?? 203,
                    birthDate: ds(crew.birthDate, DateFormat.SystemDate),
                    birthCode: crew.isOneTime ? crew.nonMemberBirthCode?.replace('/', '') : null,
                    address: (crew.nonMemberAddress === undefined || crew.nonMemberAddress === null)
                        ? null
                        : crew.nonMemberAddress.trim(),
                    street: crew.nonMemberStreet,
                    houseNumber: crew.nonMemberHouseNumber,
                    orientationNumber: crew.nonMemberOrientationNumber,
                    registrationNumber: crew.nonMemberRegistrationNumber,
                    zip: crew.nonMemberZip,
                    city: crew.nonMemberCity,
                    note: crew.note,
                })),
            })),
        })),
        storeToCloud: {
            ctlId: checkedEvent.ctlId,
            eventTitle: checkedEvent.title,
            workFile: data,
            scorers: checkedEvent.scorerRegIds,
        },
    }

    if (!provisional) {
        payload.digests = {}
        payload.digests.event = await computeDigest(payload.event)
        payload.digests.results = await computeDigest(payload.results)
    }

    apiRequest(useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken))
        .post('/events/' + checkedEvent.ctlId + '/' + (provisional ? 'provisional-' : '') + 'results', payload)
        .then((response) => {
            if (response.status === 202) {
                event.value?.setModified(false)

                notif({
                    spinner: false,
                    icon: 'sailing',
                    color: 'positive',
                    message: ts(provisional ? 'provisionalResultsUploaded' : 'resultsUploaded'),
                    timeout: 0,
                    multiLine: true,
                    classes: 'q-pa-lg text-subtitle1',
                    actions: [{
                        label: ts('close'),
                        color: 'white',
                    }],
                })
            }
        })
        .catch((err) => {
            const errorMsg = err?.response?.data?.error

            notif({
                spinner: false,
                icon: 'error',
                color: 'negative',
                message: ts('cannotUploadResults'),
                caption: errorMsg,
                timeout: 0,
                multiLine: true,
                classes: 'q-pa-lg text-subtitle1',
                actions: [{
                    label: ts('close'),
                    color: 'white',
                }],
            })
        })
}

function sendToWs() {
    const xrr = 'https://www.sailing.org/uploads/xml/sailingXRR_v2.0.1.xsd'
    const xsi = 'http://www.w3.org/2001/XMLSchema-instance'

    const myEvent = useRepo(EventsRepo).getEvent(event.value.id, true)

    const content = create({ version: '1.0', encoding: 'UTF-8' })
        .ele(xrr, 'xrr:RegattaResult')
        .att(xsi, 'xsi:schemaLocation', 'https://www.sailing.org/uploads/xml/sailingXRR_v2.0.1.xsd')
        .att(xrr, 'DateTime', new Date().toISOString())
        .att(xrr, 'Version', '2.0.1')
        .ele(myEvent.referees.map((referee) => ({
            ['Person@' + xrr]: {
                ['@PersonID@' + xrr]: referee.id,
                ['@IFPersonID@' + xrr]: referee.wsId?.emptyAsNull(),
                ['@FamilyName@' + xrr]: referee.lastName,
                ['@GivenName@' + xrr]: referee.firstName,
                ['@Gender@' + xrr]: referee.sex === 'male' ? 'Male' : 'Female',
                ['@NOC@' + xrr]: referee.refereeId !== null ? 'CZE' : null,
                ['@ContactPhone@' + xrr]: referee.phone?.formatPhoneStr().emptyAsNull(),
                ['@ContactEmail@' + xrr]: referee.email?.emptyAsNull(),
                ...(referee.refereeId !== null
                    ? {
                        ['RaceOfficialCertification@' + xrr]: referee.referee.titles.filter((
                            title,
                        ) => (title.shortcut.charAt(0) === 'I')).map((title) => ({
                            ['@NOC@' + xrr]: 'CZE',
                            ['@Type@' + xrr]: title.internationalName,
                        })),
                    }
                    : {}),
            },
        })))
        .up()
        .ele(myEvent.boats.map((boat) => (boat.crew.map((crew) => ({
            ['Person@' + xrr]: {
                ['@PersonID@' + xrr]: crew.id,
                ['@IFPersonID@' + xrr]: crew.wsId?.emptyAsNull(),
                ['@FamilyName@' + xrr]: crew.lastName,
                ['@GivenName@' + xrr]: crew.firstName,
                ['@Gender@' + xrr]: crew.sex === 'male' ? 'Male' : 'Female',
                ['@NOC@' + xrr]: crew.noc,
                ['@ContactPhone@' + xrr]: crew.phone?.formatPhoneStr().emptyAsNull(),
                ['@ContactEmail@' + xrr]: crew.email?.emptyAsNull(),
                ['PersonDetails@' + xrr]: {
                    ['@DateOfBirth@' + xrr]: ds(crew.birthDate, DateFormat.SystemDate),
                    ['@MNAMemberNumber@' + xrr]: crew.isForeign ? null : crew.regId,
                    ['@YachtClub@' + xrr]: crew.isForeign ? null : crew.visibleClub,
                },
            },
        })))))
        .up()
        .ele(myEvent.boats.map((boat) => ({
            ['Boat@' + xrr]: {
                ['@BoatID@' + xrr]: boat.id,
                ['@BoatName@' + xrr]: boat.name?.emptyAsNull(),
                ['@SailNumber@' + xrr]: boat.sailNo,
            },
        })))
        .up()
        .ele(myEvent.boats.map((boat) => ({
            ['Team@' + xrr]: {
                ['@TeamID@' + xrr]: 'T#' + boat.id,
                ['@BoatID@' + xrr]: boat.id,
                ['@Gender@' + xrr]: boat.sex === 'male' ? 'Men' : (boat.sex === 'female' ? 'Women' : 'Mixed'),
                ['Crew@' + xrr]: boat.crew.map((crew) => ({
                    ['@PersonID@' + xrr]: crew.id,
                    ['@Position@' + xrr]: crew.captain ? 'S' : 'C',
                })),
            },
        })))
        .up()
        .ele(xrr, 'Regatta')
        .att(xrr, 'RegattaID', 'CTL' + myEvent.ctlId)
        .att(xrr, 'IFRegattaID', myEvent.wsEventId)
        .att(xrr, 'Type', 'IndividualRegatta')
        .att(xrr, 'Title', myEvent.title)
        .att(xrr, 'IFUploadCode', 'tNRj781ojC')
        .ele(myEvent.races.map((race) => ({
            ['Race@' + xrr]: {
                ['@RaceID@' + xrr]: race.id,
                ['@RaceNumber@' + xrr]: race.race,
                ['@RaceStatus@' + xrr]: 'final',
            },
        })))
        .up()
        .ele(myEvent.classes.map((eventClass) => ({
            ['Division@' + xrr]: {
                ['@Type@' + xrr]: 'FleetRace',
                ['@DivisionID@' + xrr]: eventClass.id,
                ['@Title@' + xrr]: eventClass.eventClassName,
                ['@IFClassID@' + xrr]: eventClass.boatClass.intClassId?.emptyAsNull(),
                ['@StartingDivision@' + xrr]: 'true',
                ['@RankingDivision@' + xrr]: 'true',
                ['RaceResult@' + xrr]: myEvent.results(eventClass.boatClass.id).map((
                    boat,
                ) => (boat.results.map((result) => ({
                    ['@RaceID@' + xrr]: result.raceId,
                    ['@TeamID@' + xrr]: 'T#' + boat.boat.id,
                    ['@RaceRank@' + xrr]: isPositiveNumber(result.result) ? result.result : null,
                    ['@RacePoints@' + xrr]: result.points,
                    ['@Discard@' + xrr]: result.isDiscarded ? 'true' : 'false',
                    ['@ScoreCode@' + xrr]: !isPositiveNumber(result.result) ? result.result : null,
                    ['@RecordingMethod@' + xrr]: 'manual',
                })))),
            },
        })))
        .up()
        .ele(myEvent.boats.map((boat) => ({
            ['Entry@' + xrr]: {
                ['@EntryID@' + xrr]: 'E#' + boat.id,
                ['@RankingDivisionID@' + xrr]: boat.eventClass.id,
                ['@TeamID@' + xrr]: 'T#' + boat.id,
                ['@RegistrationComplete@' + xrr]: 'true',
            },
        })))
        .up()
        .ele(xrr, 'Officials')
        .ele(myEvent.referees.map((referee) => ({
            ['Official@' + xrr]: {
                ['@PersonID@' + xrr]: referee.id,
                ['@OfficiatingRole@' + xrr]: referee.refereeRole.internationalName,
            },
        })))
        .up()
        .up()
        .end({ prettyPrint: true })

    // TODO reditel?

    const blob = new Blob([content], { type: 'application/xml' })

    saveAs(blob, 'xrr-event-' + event.value.ctlId + '.xml')
}
</script>
